import { readWorkspacePrefsSync, type WorkspacePrefs } from '../hooks/useWorkspacePrefs.js';
import { projectContextStore } from '../stores/projectContext.js';
import { useThemeStore } from '../stores/theme.store.js';
import { useUXControlStore } from '../stores/uxControl.store.js';
import { getProjectMemorySnapshot, projectMemoryStore } from '../stores/projectMemory.store.js';

const LS_KEY = 'r2.settings.persistence.v1';
const BACKUP_VERSION = 1;

export interface R2SettingsSnapshot {
  version: number;
  savedAt: string;
  themePreference: unknown;
  workspaceLayout: Partial<WorkspacePrefs>;
  lastOpenedProject: string | null;
  navigationPreferences: unknown;
  aiPreferences: Record<string, unknown>;
  userProfileSettings: Record<string, unknown>;
  windowState: Record<string, unknown> | null;
  projectMemory?: Record<string, unknown> | null;
}

declare global {
  interface Window {
    r2Settings?: {
      get: () => Promise<Record<string, unknown>>;
      save: (snapshot: R2SettingsSnapshot) => Promise<Record<string, unknown>>;
      export: () => Promise<{ canceled?: boolean; success?: boolean; filePath?: string; error?: string }>;
      import: () => Promise<{ canceled?: boolean; success?: boolean; snapshot?: R2SettingsSnapshot; error?: string }>;
      saveWindowState: () => Promise<Record<string, unknown>>;
    };
  }
}

function readJson<T>(key: string, fallback: T): T {
  try { return JSON.parse(localStorage.getItem(key) || '') as T; } catch { return fallback; }
}

function writeLocal(snapshot: R2SettingsSnapshot): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(snapshot)); } catch { /* quota guard */ }
}

function readLocal(): R2SettingsSnapshot | null {
  return readJson<R2SettingsSnapshot | null>(LS_KEY, null);
}

export function collectSettingsSnapshot(windowState: Record<string, unknown> | null = null): R2SettingsSnapshot {
  const ux = useUXControlStore.getState();
  const theme = useThemeStore.getState();
  return {
    version: BACKUP_VERSION,
    savedAt: new Date().toISOString(),
    themePreference: {
      activeTheme: theme.activeTheme,
      customVars: theme.customVars,
      autoSwitch: theme.autoSwitch,
      projectThemes: theme.projectThemes,
      accentOverride: theme.accentOverride,
      cornerRadius: theme.cornerRadius,
      transparency: theme.transparency,
      shadowIntensity: theme.shadowIntensity,
      glowIntensity: theme.glowIntensity,
      animationLevel: theme.animationLevel,
    },
    workspaceLayout: readWorkspacePrefsSync(),
    lastOpenedProject: projectContextStore.getState().activeProjectId,
    navigationPreferences: {
      activeProfile: ux.activeProfile,
      sidebarMode: ux.sidebarMode,
      rightClickEnabled: ux.rightClickEnabled,
      rightClickCommands: ux.rightClickCommands,
      radialMenuEnabled: ux.radialMenuEnabled,
      radialHoldMs: ux.radialHoldMs,
      customButtons: ux.customButtons,
      usageRecords: ux.usageRecords,
    },
    aiPreferences: readJson<Record<string, unknown>>('r2.ai.preferences.v1', { provider: 'default', mode: 'engineering', confidenceLabels: true }),
    userProfileSettings: {
      lastLoginEmail: localStorage.getItem('r2:lastLoginEmail') ?? null,
      founderMode: Boolean(localStorage.getItem('rcd2.founder.token')),
      language: localStorage.getItem('r2.language') ?? 'en',
    },
    windowState,
    projectMemory: getProjectMemorySnapshot() as unknown as Record<string, unknown>,
  };
}

export async function saveSettingsSnapshot(reason = 'auto'): Promise<R2SettingsSnapshot> {
  let windowState: Record<string, unknown> | null = null;
  try { windowState = (await window.r2Settings?.saveWindowState?.()) ?? null; } catch { /* web preview */ }
  const snapshot = collectSettingsSnapshot({ ...(windowState ?? {}), reason });
  writeLocal(snapshot);
  try { await window.r2Settings?.save?.(snapshot); } catch { /* web preview */ }
  return snapshot;
}

export async function restoreSettingsSnapshot(snapshot?: R2SettingsSnapshot | null): Promise<R2SettingsSnapshot | null> {
  let restored = snapshot ?? null;
  if (!restored) {
    try {
      const native = await window.r2Settings?.get?.();
      restored = (native?.snapshot as R2SettingsSnapshot | undefined) ?? null;
    } catch { /* web preview */ }
  }
  restored = restored ?? readLocal();
  if (!restored) return null;
  writeLocal(restored);
  if (restored.lastOpenedProject) projectContextStore.setActiveProject(restored.lastOpenedProject);
  if (restored.projectMemory && typeof restored.projectMemory === 'object') {
    const memory = restored.projectMemory as { userId?: string; projectId?: string };
    projectMemoryStore.getState().hydrate(memory.userId, memory.projectId || restored.lastOpenedProject || undefined);
  }
  if (restored.themePreference && typeof restored.themePreference === 'object') {
    const theme = restored.themePreference as Record<string, unknown>;
    if (typeof theme.activeTheme === 'string') useThemeStore.getState().setTheme(theme.activeTheme as never);
  }
  return restored;
}

export async function exportSettingsBackup(): Promise<void> {
  const snapshot = await saveSettingsSnapshot('backup');
  if (window.r2Settings?.export) {
    const result = await window.r2Settings.export();
    if (result?.success || result?.canceled) return;
  }
  const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `r2-settings-backup-${Date.now()}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function importSettingsBackup(file?: File): Promise<R2SettingsSnapshot | null> {
  let snapshot: R2SettingsSnapshot | null = null;
  if (file) snapshot = JSON.parse(await file.text()) as R2SettingsSnapshot;
  else if (window.r2Settings?.import) {
    const result = await window.r2Settings.import();
    snapshot = result?.snapshot ?? null;
  }
  if (!snapshot) return null;
  writeLocal(snapshot);
  try { await window.r2Settings?.save?.(snapshot); } catch { /* preview */ }
  return restoreSettingsSnapshot(snapshot);
}

export function installSettingsAutosave(): () => void {
  const unsubTheme = useThemeStore.subscribe(() => { void saveSettingsSnapshot('theme-change'); });
  const unsubUx = useUXControlStore.subscribe(() => { void saveSettingsSnapshot('navigation-change'); });
  const onProject = () => { void saveSettingsSnapshot('project-change'); };
  window.addEventListener('r2:project-active-changed', onProject);
  const onBeforeUnload = () => { void saveSettingsSnapshot('shutdown'); };
  window.addEventListener('beforeunload', onBeforeUnload);
  void restoreSettingsSnapshot();
  void saveSettingsSnapshot('startup');
  return () => {
    unsubTheme(); unsubUx();
    window.removeEventListener('r2:project-active-changed', onProject);
    window.removeEventListener('beforeunload', onBeforeUnload);
  };
}